import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CONSENT_VERSION,
  detectRegion,
  loadConsent,
  maybeInitGA4,
  saveConsent,
  type Region,
  type StoredConsent,
} from "@/lib/cookieConsent";

const OPEN_EVENT = "cookie-consent-open";
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<Region>("eu");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = loadConsent();
      const r = await detectRegion();
      if (cancelled) return;
      setRegion(r);
      if (stored && stored.version === CONSENT_VERSION) {
        // Load GA4 (dormant unless ID + analytics consent).
        maybeInitGA4();
      } else {
        setOpen(true);
      }
    })();
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      cancelled = true;
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  const persist = (analyticsChoice: boolean) => {
    const consent: StoredConsent = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      region,
      categories: { necessary: true, analytics: analyticsChoice },
    };
    saveConsent(consent);
    if (analyticsChoice) maybeInitGA4();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:max-w-lg z-50 rounded-xl border border-neon-green/40 bg-card/95 backdrop-blur-xl p-5 shadow-2xl glow-green"
    >
      <h2 id="cookie-consent-title" className="font-display text-lg text-neon-green mb-2">
        We use cookies
      </h2>
      <p className="text-sm text-foreground/90 mb-4">
        We use strictly necessary cookies to run this site and process payments securely.
        With your consent, we may also use analytics cookies to improve the Service. See our{" "}
        <Link to="/cookies" className="underline text-neon-green">Cookie Policy</Link> and{" "}
        <Link to="/privacy" className="underline text-neon-green">Privacy Policy</Link>.
        {region === "us" && (
          <span className="block mt-2 text-xs text-muted-foreground">
            US visitors: click "Reject All" to exercise your right to opt out — Do Not Sell or Share My Personal Information.
          </span>
        )}
      </p>

      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={() => persist(true)}
          className="bg-neon-green text-background hover:bg-neon-green/90 font-bold w-full"
        >
          Accept All
        </Button>
        <Button
          onClick={() => persist(false)}
          className="bg-neon-purple text-background hover:bg-neon-purple/90 font-bold w-full"
        >
          Reject All
        </Button>
      </div>
    </div>
  );
};
